import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useOne, useRemove } from "@/hooks/crud";
import { Spinner, ErrorBanner, Empty } from "@/components/Feedback";
import { StageBadge, RemixBadge } from "@/components/Badges";
import { TrackForm } from "./TrackForm";
import { ConceptPanel } from "./sections/ConceptPanel";
import { PromptsPanel } from "./sections/PromptsPanel";
import { AssetsPanel } from "./sections/AssetsPanel";
import { TagsPanel } from "./sections/TagsPanel";
import { MasterPanel } from "./sections/MasterPanel";
import { ReleasePanel } from "./sections/ReleasePanel";
import { formatNumber, msToClock, formatDate } from "@/lib/format";
import type { Track } from "@/types/database";

type TrackRow = Track & {
  artists?: { name: string } | null;
  projects?: { name: string } | null;
};

export default function TrackDetail() {
  const { id } = useParams();
  const trackId = Number(id);
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);

  const { data, isLoading, error } = useOne(
    "tracks",
    Number.isFinite(trackId) ? trackId : null,
    "select=*,artists(name),projects(name)",
  );
  const remove = useRemove("tracks");

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBanner error={error} />;
  if (!data) return <Empty>Track not found.</Empty>;

  const track = data as TrackRow;

  async function onDelete() {
    if (!confirm(`Delete "${track.title}" and all its pipeline records?`)) return;
    await remove.mutateAsync(trackId);
    navigate("/tracks");
  }

  return (
    <div>
      <div className="breadcrumb">
        <Link to="/tracks">Tracks</Link> / {track.title}
      </div>
      <div className="page-head">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {track.title} {track.is_remix && <RemixBadge />}
          </h1>
          <div className="page-sub">
            {track.artists?.name ?? "—"}
            {track.projects?.name ? ` · ${track.projects.name}` : ""}
            {track.ep_name ? ` · EP: ${track.ep_name}` : ""}
          </div>
        </div>
        <div className="row-actions">
          <StageBadge stage={track.pipeline_stage} />
          <button className="btn btn-sm" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button className="btn btn-sm btn-danger" onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>

      <div className="detail-grid">
        <div>
          <ConceptPanel trackId={trackId} />
          <PromptsPanel trackId={trackId} />
          <AssetsPanel trackId={trackId} />
        </div>

        <div>
          <div className="panel">
            <div className="panel-head">
              <h3>Track</h3>
            </div>
            <div className="panel-body">
              <dl className="kv">
                <dt>Stage</dt>
                <dd>
                  <StageBadge stage={track.pipeline_stage} />
                </dd>
                <dt>Genre</dt>
                <dd>{track.primary_genre ?? "—"}</dd>
                <dt>BPM</dt>
                <dd className="mono">{track.bpm ?? "—"}</dd>
                <dt>Key</dt>
                <dd className="mono">{track.key_signature ?? "—"}</dd>
                <dt>Duration</dt>
                <dd className="mono">{msToClock(track.duration_ms)}</dd>
                <dt>Source</dt>
                <dd>{track.source}</dd>
                <dt>Plays</dt>
                <dd className="mono">{formatNumber(track.sc_playback_count)}</dd>
                <dt>Likes</dt>
                <dd className="mono">{formatNumber(track.sc_favoritings_count)}</dd>
                <dt>SC date</dt>
                <dd>{formatDate(track.sc_created_at)}</dd>
                <dt>SoundCloud</dt>
                <dd>
                  {track.sc_permalink_url ? (
                    <a href={track.sc_permalink_url} target="_blank" rel="noreferrer">
                      open
                    </a>
                  ) : (
                    "—"
                  )}
                </dd>
              </dl>
              {track.notes && (
                <p className="muted" style={{ marginTop: 12, fontSize: 13 }}>
                  {track.notes}
                </p>
              )}
            </div>
          </div>

          <TagsPanel trackId={trackId} />
          <MasterPanel trackId={trackId} />
          <ReleasePanel trackId={trackId} />
        </div>
      </div>

      {editing && <TrackForm track={track} onClose={() => setEditing(false)} />}
    </div>
  );
}
